<template>
  <div class="customer-sources">
    <v-card class="mb-5 rounded-lg elevation-2">
      <v-card-title class="py-4 d-flex flex-wrap align-center">
        <div>
          <h4 class="text-h5 font-weight-medium mb-1">Phân loại khách hàng theo nguồn</h4>
          <div class="text-body-2 grey--text">Quản lý và gán nguồn tiếp cận cho từng khách hàng</div>
        </div>
        <v-spacer />
        <v-btn color="primary" class="text-none" @click="createDialog = true">
          <v-icon left>mdi-plus</v-icon>Thêm nguồn mới
        </v-btn>
      </v-card-title>
    </v-card>

    <v-card class="mb-5 rounded-lg elevation-1">
      <v-card-text>
        <div class="text-subtitle-1 font-weight-medium mb-3">Các nguồn khách hàng</div>
        <div class="d-flex flex-wrap">
          <v-chip
            v-for="source in sources"
            :key="source.id"
            class="mr-2 mb-2"
            :color="filters.sourceId === source.id ? 'primary' : 'grey lighten-3'"
            :text-color="filters.sourceId === source.id ? 'white' : ''"
            @click="selectSource(source.id)"
          >
            <v-icon left small>{{ sourceIcon(source.name) }}</v-icon>
            {{ source.name }} ({{ source.customers_count || 0 }})
            <v-icon v-if="!source.is_default" right small @click.stop="confirmDelete(source)">mdi-close</v-icon>
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="rounded-lg elevation-3">
      <v-card-title class="py-4">
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field v-model="search" label="Tìm theo tên hoặc số điện thoại" prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="filters.sourceId" :items="sourceFilterItems" item-text="name" item-value="id" label="Lọc theo nguồn" prepend-inner-icon="mdi-source-branch" outlined dense clearable hide-details @change="applyFilter" />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn block outlined color="primary" class="text-none" @click="resetFilters"><v-icon left>mdi-refresh</v-icon>Đặt lại bộ lọc</v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="customers"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
      >
        <template v-slot:item.customer="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
          <div class="text-caption grey--text"><v-icon x-small>mdi-phone</v-icon> {{ item.phone }}</div>
        </template>
        <template v-slot:item.source="{ item }">
          <v-select
            :value="item.customer_source_id"
            :items="sources"
            item-text="name"
            item-value="id"
            dense outlined hide-details
            :loading="updatingId === item.id"
            @change="assignSource(item, $event)"
          >
            <template v-slot:selection="{ item: source }">
              <v-icon small class="mr-2">{{ sourceIcon(source.name) }}</v-icon>{{ source.name }}
            </template>
          </v-select>
        </template>
        <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
        <template v-slot:no-data><div class="py-8 grey--text">Không có khách hàng phù hợp</div></template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="createDialog" max-width="440">
      <v-card>
        <v-card-title>Thêm nguồn khách hàng</v-card-title>
        <v-card-text>
          <v-text-field v-model.trim="newSourceName" label="Tên nguồn (VD: Shopee, Facebook)" outlined autofocus :rules="[v => !!v || 'Vui lòng nhập tên nguồn']" @keyup.enter="createSource" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Hủy</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!newSourceName" @click="createSource">Thêm nguồn</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card>
        <v-card-title>Xóa nguồn khách hàng?</v-card-title>
        <v-card-text>Khách hàng thuộc nguồn <b>{{ sourceToDelete && sourceToDelete.name }}</b> sẽ được chuyển về TikTok.</v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="deleteDialog = false">Hủy</v-btn><v-btn color="error" :loading="deleting" @click="deleteSource">Xóa</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import sourceModule from '@/api/customerSources';

const sourceActions = sourceModule.actions;

export default {
  name: 'CustomerSources',
  data() {
    return {
      sources: [], customers: [], totalItems: 0,
      search: '', searchTimer: null, loading: false, updatingId: null,
      saving: false, deleting: false, createDialog: false, deleteDialog: false,
      newSourceName: '', sourceToDelete: null,
      filters: { sourceId: null },
      options: { page: 1, itemsPerPage: 10 },
      headers: [
        { text: 'Khách hàng', value: 'customer', sortable: false },
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Địa chỉ', value: 'address', sortable: false },
        { text: 'Nguồn khách hàng', value: 'source', width: '240px', sortable: false },
        { text: 'Ngày tạo', value: 'created_at', sortable: false },
      ],
    };
  },
  computed: {
    sourceFilterItems() { return [{ id: null, name: 'Tất cả nguồn' }, ...this.sources]; },
  },
  watch: {
    options: { deep: true, handler() { this.fetchCustomers(); } },
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.options.page = 1; this.fetchCustomers(); }, 500);
    },
  },
  mounted() { this.loadPage(); },
  methods: {
    async loadPage() { await this.fetchSources(); await this.fetchCustomers(); },
    async fetchSources() {
      try { const response = await sourceActions.getSources(); this.sources = response.data.data || []; }
      catch (error) { this.$snackbar.error('Không thể tải danh sách nguồn khách hàng'); }
    },
    async fetchCustomers() {
      this.loading = true;
      try {
        const response = await sourceActions.getCustomers({ search: this.search || undefined, source_id: this.filters.sourceId || undefined, page: this.options.page, per_page: this.options.itemsPerPage });
        this.customers = response.data.data || [];
        this.totalItems = Number(response.data.meta?.total || 0);
      } catch (error) { this.$snackbar.error(error.response?.data?.message || 'Không thể tải danh sách khách hàng'); }
      finally { this.loading = false; }
    },
    async createSource() {
      if (!this.newSourceName) return;
      this.saving = true;
      try {
        await sourceActions.createSource(this.newSourceName);
        this.$snackbar.success('Thêm nguồn khách hàng thành công');
        this.newSourceName = ''; this.createDialog = false; await this.fetchSources();
      } catch (error) { this.$snackbar.error(error.response?.data?.message || 'Không thể thêm nguồn khách hàng'); }
      finally { this.saving = false; }
    },
    confirmDelete(source) { this.sourceToDelete = source; this.deleteDialog = true; },
    async deleteSource() {
      this.deleting = true;
      try {
        await sourceActions.deleteSource(this.sourceToDelete.id);
        if (this.filters.sourceId === this.sourceToDelete.id) this.filters.sourceId = null;
        this.deleteDialog = false; this.$snackbar.success('Đã xóa nguồn khách hàng'); await this.loadPage();
      } catch (error) { this.$snackbar.error(error.response?.data?.message || 'Không thể xóa nguồn khách hàng'); }
      finally { this.deleting = false; }
    },
    async assignSource(customer, sourceId) {
      this.updatingId = customer.id;
      try { await sourceActions.assignSource(customer.id, sourceId); customer.customer_source_id = sourceId; this.$snackbar.success('Cập nhật nguồn thành công'); await this.fetchSources(); }
      catch (error) { this.$snackbar.error(error.response?.data?.message || 'Không thể cập nhật nguồn'); await this.fetchCustomers(); }
      finally { this.updatingId = null; }
    },
    selectSource(id) { this.filters.sourceId = this.filters.sourceId === id ? null : id; this.applyFilter(); },
    applyFilter() { this.options.page = 1; this.fetchCustomers(); },
    resetFilters() { this.search = ''; this.filters.sourceId = null; this.options.page = 1; this.fetchCustomers(); },
    sourceIcon(name) {
      const value = String(name || '').toLowerCase();
      if (value.includes('facebook')) return 'mdi-facebook';
      if (value.includes('shopee')) return 'mdi-shopping';
      if (value.includes('tiktok')) return 'mdi-music-note';
      return 'mdi-source-branch';
    },
    formatDate(value) { return value ? new Date(value).toLocaleDateString('vi-VN') : '—'; },
  },
};
</script>
